import { uniq } from 'es-toolkit';
import React from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { Divider, Stack } from '@mui/material';

import { INDETERMINATE, MaterialPropertyValuePair } from '@paragon/client-tools';
import { LayoutFeatureArea } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { humanCase, MappedOmit } from '@paragon/utilities';

import { TransformationButtons } from '../../../../../library/transformations/controls/TransformationButtons';
import { editFeatureAreas } from '../../../../../store/actions/layout/featureAreaActions';
import { getAllFeaturePatternContainers } from '../../../../../store/selectors/layoutDataSelectors';
import { isLayoutModeViewOnly } from '../../../../../store/selectors/layoutModeSelectors';
import { propertiesShouldBeViewOnly } from '../../../../../store/selectors/viewOnlyModeSelectors';
import { FeaturePatternContainerSelect } from '../../../../controls/FeaturePatternContainerSelect';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { PropertiesPanelMenuButton } from '../PropertiesPanelMenuButton';
import { FeatureAreaRectangleControls } from './comon/FeatureAreaRectangleControls';
import { FeatureAreaUniquePropertyControls } from './FeatureAreaUniquePropertyControls';

const shouldBeViewOnly = createSelector(
  [propertiesShouldBeViewOnly, isLayoutModeViewOnly],
  (viewOnlyProperties, viewOnlyLayout) => viewOnlyProperties || viewOnlyLayout,
);

export function FeatureAreaPropertiesPanel(props: { value: LayoutFeatureArea }) {
  const dispatch = useAppDispatch();
  const viewOnly = useAppSelector(shouldBeViewOnly);

  const container = useAppSelector(getAllFeaturePatternContainers).find((container) =>
    container.featureAreas.some((area) => area.guid === props.value.guid),
  );
  if (container == null) return null;

  async function applyEdit(editedArea: MappedOmit<LayoutFeatureArea, 'guid'>) {
    await dispatch(editFeatureAreas({ guid: props.value.guid, containerGuid: container!.guid, editedArea }));
  }

  return (
    <PropertiesPanel
      title={`${humanCase(props.value.featureType)} Properties`}
      titleButton={<PropertiesPanelMenuButton type="featureArea" guid={props.value.guid} />}
    >
      <Stack spacing={2} direction="column">
        <MaterialPropertyValuePair property="Type" value={humanCase(props.value.featureType)} />

        <FeaturePatternContainerSelect
          featurePatternContainerGuid={container.guid}
          disabled={viewOnly}
          onChange={async (containerGuid) => {
            await dispatch(editFeatureAreas({ guid: props.value.guid, containerGuid, editedArea: props.value }));
          }}
        />

        <Divider />

        <FeatureAreaRectangleControls value={props.value} disabled={viewOnly} onChange={applyEdit} />

        <Divider />

        <FeatureAreaUniquePropertyControls value={props.value} disabled={viewOnly} onChange={applyEdit} />

        <Divider />

        <TransformationButtons />
      </Stack>
    </PropertiesPanel>
  );
}

export function BulkFeatureAreaPropertiesPanel(props: { values: LayoutFeatureArea[] }) {
  const dispatch = useAppDispatch();
  const viewOnly = useAppSelector(shouldBeViewOnly);

  const allFeaturePatternContainers = useAppSelector(getAllFeaturePatternContainers);

  const relatedFeaturePatternContainers = allFeaturePatternContainers.filter((container) =>
    props.values.some((area) => container.featureAreas.some((featureArea) => featureArea.guid === area.guid)),
  );
  const featurePatternContainerGuids = uniq(relatedFeaturePatternContainers.map((container) => container.guid));
  const commonFeaturePatternContainerGuid =
    featurePatternContainerGuids.length === 1 ? featurePatternContainerGuids[0] : INDETERMINATE;

  return (
    <PropertiesPanel
      title="Multiple Feature Area Properties"
      titleButton={<PropertiesPanelMenuButton type="featureArea" guid={props.values[0].guid} />}
    >
      <FeaturePatternContainerSelect
        sx={{ marginTop: 3, marginBottom: 2 }}
        featurePatternContainerGuid={commonFeaturePatternContainerGuid}
        disabled={viewOnly}
        onChange={async (containerGuid) => {
          await dispatch(
            editFeatureAreas(
              props.values.map((area) => ({
                guid: area.guid,
                containerGuid,
                editedArea: area,
              })),
            ),
          );
        }}
      />

      <Divider />

      <TransformationButtons />
    </PropertiesPanel>
  );
}
